import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { Settings, Link2, Unlink, Loader2, CheckCircle, AlertTriangle, CreditCard, ArrowRight, FileText, Users, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API_BASE}/api`, headers: { 'Content-Type': 'application/json' } });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('at1s_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const AdminSettings = () => {
  const { toast } = useToast();
  const [qboStatus, setQboStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [showDisconnect, setShowDisconnect] = useState(false);
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [migrating, setMigrating] = useState(false);

  // Customer sync state
  const [customerSyncResult, setCustomerSyncResult] = useState(null);
  const [customerSyncing, setCustomerSyncing] = useState(false);
  const [customerList, setCustomerList] = useState(null);
  const [customerListLoading, setCustomerListLoading] = useState(false);

  useEffect(() => {
    loadStatus();
    // Check URL params for QBO callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('qbo') === 'connected') {
      toast({ title: 'QuickBooks Connected', description: 'Your QuickBooks account has been linked.' });
      window.history.replaceState({}, '', '/admin/settings');
    }
  }, []);

  const loadStatus = async () => {
    try {
      const { data } = await api.get('/qbo/status');
      setQboStatus(data);
    } catch {
      setQboStatus({ connected: false });
    } finally { setLoading(false); }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get('/qbo/connect', { params: { origin: window.location.origin } });
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      }
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to connect', variant: 'destructive' });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post('/qbo/disconnect');
      toast({ title: 'Disconnected', description: 'QuickBooks has been disconnected.' });
      setShowDisconnect(false);
      loadStatus();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><Loader2 className="h-10 w-10 animate-spin text-blue-600" /></div></Layout>;

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl" data-testid="admin-settings-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="h-6 w-6 text-slate-600" /> Settings
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage integrations and app configuration</p>
        </div>

        {/* QuickBooks Connection */}
        <Card data-testid="qbo-connection-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-green-600" /> Payment Processing — QuickBooks
            </CardTitle>
          </CardHeader>
          <CardContent>
            {qboStatus?.connected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
                  <CheckCircle className="h-8 w-8 text-green-600 shrink-0" />
                  <div>
                    <p className="font-bold text-green-800">QuickBooks Connected</p>
                    <p className="text-sm text-green-700">Realm ID: {qboStatus.realm_id}</p>
                    {qboStatus.connected_at && <p className="text-xs text-green-600">Connected: {new Date(qboStatus.connected_at).toLocaleString()}</p>}
                  </div>
                </div>
                <p className="text-sm text-slate-600">
                  All customer payments are processed through QuickBooks Payments. Credit card charges are sent directly to your QuickBooks account.
                </p>
                <Button variant="outline" onClick={() => setShowDisconnect(true)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="disconnect-qbo-btn">
                  <Unlink className="h-4 w-4 mr-2" /> Disconnect QuickBooks
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <AlertTriangle className="h-8 w-8 text-amber-600 shrink-0" />
                  <div>
                    <p className="font-bold text-amber-800">QuickBooks Not Connected</p>
                    <p className="text-sm text-amber-700">Connect your QuickBooks account to process payments.</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600">
                  Link your QuickBooks Online account to enable credit card and ACH payments. Customer payments will be deposited directly into your QuickBooks-connected bank account.
                </p>
                <Button onClick={handleConnect} disabled={connecting} className="bg-green-600 hover:bg-green-700" data-testid="connect-qbo-btn">
                  {connecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                  Connect QuickBooks
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Migration Tool — only show when connected */}
        {qboStatus?.connected && (
          <Card data-testid="qbo-migration-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" /> Migrate Orders to QuickBooks
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Sync all existing orders to QuickBooks as invoices. Paid orders will also have a payment recorded in QBO.
              </p>

              {!migrationPreview && !migrationResult && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    setMigrationLoading(true);
                    try {
                      const { data } = await api.get('/qbo/migrate/preview');
                      setMigrationPreview(data);
                    } catch (e) {
                      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to load preview', variant: 'destructive' });
                    } finally { setMigrationLoading(false); }
                  }}
                  disabled={migrationLoading}
                  data-testid="preview-migration-btn"
                >
                  {migrationLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                  Preview Migration
                </Button>
              )}

              {migrationPreview && !migrationResult && (
                <div className="space-y-3">
                  <div className="bg-slate-50 border rounded-xl p-4 space-y-2">
                    <h4 className="font-semibold text-slate-800">Migration Preview</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-slate-500">Total Orders:</span> <strong>{migrationPreview.total_orders}</strong></div>
                      <div><span className="text-slate-500">Paid Orders:</span> <strong className="text-green-700">{migrationPreview.paid_orders}</strong></div>
                      <div><span className="text-slate-500">Unpaid Orders:</span> <strong className="text-amber-700">{migrationPreview.unpaid_orders}</strong></div>
                      <div><span className="text-slate-500">Paid Total:</span> <strong className="text-green-700">${migrationPreview.paid_total?.toFixed(2)}</strong></div>
                    </div>
                  </div>

                  {migrationPreview.total_orders === 0 ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" /> All orders already synced to QuickBooks.
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        onClick={async () => {
                          setMigrating(true);
                          try {
                            const { data } = await api.post('/qbo/migrate/run');
                            setMigrationResult(data);
                            toast({ title: 'Migration Complete', description: data.message });
                          } catch (e) {
                            toast({ title: 'Error', description: e.response?.data?.detail || 'Migration failed', variant: 'destructive' });
                          } finally { setMigrating(false); }
                        }}
                        disabled={migrating}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="run-migration-btn"
                      >
                        {migrating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Migrating...</> : <><ArrowRight className="h-4 w-4 mr-2" /> Run Migration</>}
                      </Button>
                      <Button variant="outline" onClick={() => setMigrationPreview(null)}>Cancel</Button>
                    </div>
                  )}
                </div>
              )}

              {migrationResult && (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <h4 className="font-semibold text-blue-800 mb-2">Migration Results</h4>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="bg-green-100 rounded-lg p-2 text-center">
                        <p className="text-2xl font-bold text-green-700">{migrationResult.synced}</p>
                        <p className="text-xs text-green-600">Synced</p>
                      </div>
                      <div className="bg-red-100 rounded-lg p-2 text-center">
                        <p className="text-2xl font-bold text-red-700">{migrationResult.failed}</p>
                        <p className="text-xs text-red-600">Failed</p>
                      </div>
                      <div className="bg-slate-100 rounded-lg p-2 text-center">
                        <p className="text-2xl font-bold text-slate-700">{migrationResult.skipped}</p>
                        <p className="text-xs text-slate-600">Skipped</p>
                      </div>
                    </div>
                  </div>
                  {migrationResult.details?.filter(d => d.status === 'failed').length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm font-semibold text-red-700 mb-1">Failed Orders:</p>
                      {migrationResult.details.filter(d => d.status === 'failed').map((d, i) => (
                        <p key={i} className="text-xs text-red-600">{d.order_id}: {d.error}</p>
                      ))}
                    </div>
                  )}
                  <Button variant="outline" onClick={() => { setMigrationResult(null); setMigrationPreview(null); }}>Done</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Customer Sync to QBO — only show when connected */}
        {qboStatus?.connected && (
          <Card data-testid="qbo-customer-sync-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" /> Sync Customers to QuickBooks
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Push all your customer contacts to QuickBooks Online. Merges data from user accounts and customer master records.
              </p>

              <div className="flex gap-2">
                <Button
                  onClick={async () => {
                    setCustomerSyncing(true);
                    setCustomerSyncResult(null);
                    try {
                      const { data } = await api.post('/qbo/sync-customers');
                      setCustomerSyncResult(data);
                      toast({ title: 'Customer Sync Complete', description: data.message });
                    } catch (e) {
                      toast({ title: 'Error', description: e.response?.data?.detail || 'Sync failed', variant: 'destructive' });
                    } finally { setCustomerSyncing(false); }
                  }}
                  disabled={customerSyncing}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="sync-customers-btn"
                >
                  {customerSyncing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Syncing...</> : <><RefreshCw className="h-4 w-4 mr-2" /> Sync All Customers</>}
                </Button>

                <Button
                  variant="outline"
                  onClick={async () => {
                    setCustomerListLoading(true);
                    try {
                      const { data } = await api.get('/qbo/customers');
                      setCustomerList(data);
                    } catch (e) {
                      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
                    } finally { setCustomerListLoading(false); }
                  }}
                  disabled={customerListLoading}
                  data-testid="view-customers-btn"
                >
                  {customerListLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Users className="h-4 w-4 mr-2" />}
                  View Sync Status
                </Button>
              </div>

              {/* Sync Results */}
              {customerSyncResult && (
                <div className="space-y-3">
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                    <h4 className="font-semibold text-purple-800 mb-2">Sync Results</h4>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="bg-green-100 rounded-lg p-2 text-center">
                        <p className="text-2xl font-bold text-green-700">{customerSyncResult.synced}</p>
                        <p className="text-xs text-green-600">Synced</p>
                      </div>
                      <div className="bg-slate-100 rounded-lg p-2 text-center">
                        <p className="text-2xl font-bold text-slate-700">{customerSyncResult.skipped}</p>
                        <p className="text-xs text-slate-600">Already in QBO</p>
                      </div>
                      <div className="bg-red-100 rounded-lg p-2 text-center">
                        <p className="text-2xl font-bold text-red-700">{customerSyncResult.failed}</p>
                        <p className="text-xs text-red-600">Failed</p>
                      </div>
                    </div>
                  </div>
                  {customerSyncResult.details?.filter(d => d.status === 'failed').length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm font-semibold text-red-700 mb-1">Failed:</p>
                      {customerSyncResult.details.filter(d => d.status === 'failed').map((d, i) => (
                        <p key={i} className="text-xs text-red-600">{d.email}: {d.error}</p>
                      ))}
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setCustomerSyncResult(null)}>Dismiss</Button>
                </div>
              )}

              {/* Customer List */}
              {customerList && (
                <div className="space-y-3">
                  <div className="bg-slate-50 border rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-800">Customers ({customerList.total})</h4>
                      <Badge variant="outline" className="text-purple-600 border-purple-200">
                        {customerList.synced}/{customerList.total} synced
                      </Badge>
                    </div>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {customerList.customers?.map((c, i) => (
                        <div key={i} className="flex items-center justify-between text-sm bg-white rounded-lg border px-3 py-2">
                          <div>
                            <p className="font-medium text-slate-800">{c.name || 'Unnamed'}</p>
                            <p className="text-xs text-slate-500">{c.email}</p>
                          </div>
                          {c.synced ? (
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              <CheckCircle className="h-3 w-3 mr-1" /> Synced
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              <AlertTriangle className="h-3 w-3 mr-1" /> Not synced
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setCustomerList(null)}>Close</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Disconnect Confirm */}
      <Dialog open={showDisconnect} onOpenChange={() => setShowDisconnect(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Disconnect QuickBooks</DialogTitle></DialogHeader>
          <DialogDescription>Are you sure? Customers won&apos;t be able to make payments until you reconnect.</DialogDescription>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisconnect(false)}>Cancel</Button>
            <Button onClick={handleDisconnect} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-disconnect-qbo">Disconnect</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminSettings;
